/**
 * Trends module — mock widget loaders.
 *
 * Every loader reads `scoped(query).own`: range → chips → own brand, which is
 * the prototype's `TrendsView` (`ownBrand` then the `default` bar's chips; the
 * same set of rows in the same order).
 */
import type { SeriesKey, WidgetLoaders } from '@/data-sources/contract';
import type {
  KeywordFrequency,
  KeywordMentionsFrequencyData,
  KeywordMentionsFrequencyParams,
  TrendHashtag,
  TrendKeyword,
} from '@/data-sources/contract/widgets/trends';

import { aggregate } from '../engine/aggregate';
import {
  HASHTAG_LABELS,
  KEYWORD_GLOSS,
  KEYWORD_LABELS,
  KEYWORD_VALUES,
  type MentionRow,
} from '../engine/generator';
import { searchKeywords, type KeywordHit } from '@/lib/analytics/keywordSearch';

import { scoped } from '../query';
import { breakdownOf } from './mentions';
import { comparisonOf, timeSeriesOf } from './shared';

export function trendKeyword(key: string): TrendKeyword {
  return { key, label: KEYWORD_LABELS[key] ?? key, gloss: KEYWORD_GLOSS[key] ?? null };
}

const trendHashtag = (key: string): TrendHashtag => ({ key, label: HASHTAG_LABELS[key] ?? key });

const topic = (key: string): SeriesKey => ({ key, dimension: 'topic' });

/**
 * The prototype's `taggedRows`: only public posts carry a tag, and the table's
 * share is of tagged mentions — so untagged rows leave the counts *and* the
 * series (`pivot` would have skipped them as categories but counted their
 * sentiments).
 */
export const taggedRows = (rows: readonly MentionRow[]) =>
  rows.filter((row) => row.hashtag !== null);

/** Per-keyword counts and topic split — the input `searchKeywords` reduced from rows. */
function keywordFrequencies(rows: readonly MentionRow[]): KeywordFrequency[] {
  const byKeyword = new Map<string, KeywordFrequency>();
  for (const row of rows) {
    let entry = byKeyword.get(row.keyword);
    if (!entry) {
      entry = {
        ...trendKeyword(row.keyword),
        index: KEYWORD_VALUES.indexOf(row.keyword as never),
        mentions: 0,
        byTopic: {},
      };
      byKeyword.set(row.keyword, entry);
    }
    entry.mentions += 1;
    entry.byTopic[row.topic] = (entry.byTopic[row.topic] ?? 0) + 1;
  }
  return [...byKeyword.values()];
}

/**
 * One page of a keyword search, as a paging API serves it: the hits in their
 * ranked order (matches by volume, then related), and the largest count among
 * them for the bars' common scale. A page past the end is clamped to the last.
 */
export function pageKeywordHits(
  hits: readonly KeywordHit[],
  params: KeywordMentionsFrequencyParams,
): KeywordMentionsFrequencyData {
  const pageSize = Math.max(1, Math.floor(params.pageSize));
  const lastPage = Math.max(1, Math.ceil(hits.length / pageSize));
  const page = Math.min(Math.max(1, Math.floor(params.page)), lastPage);
  return {
    keywords: hits
      .slice((page - 1) * pageSize, page * pageSize)
      .map((hit) => ({ ...hit.keyword, relation: hit.relation })),
    total: hits.length,
    page,
    pageSize,
    maxMentions: hits.length === 0 ? null : Math.max(...hits.map((hit) => hit.keyword.mentions)),
  };
}

export const trendsWidgetLoaders = {
  'trending-keywords': async ({ query }) => ({
    keywords: breakdownOf(scoped(query).own, (row) => row.keyword, trendKeyword, 'sentiment'),
  }),

  'keyword-sentiment': async ({ query }) => ({
    keywords: breakdownOf(scoped(query).own, (row) => row.keyword, trendKeyword, 'sentiment'),
  }),

  'trending-topics': async ({ query }) => ({
    topics: breakdownOf(scoped(query).own, (row) => row.topic, topic, 'sentiment'),
  }),

  'top-hashtags': async ({ query }) => ({
    hashtags: breakdownOf(
      taggedRows(scoped(query).own),
      (row) => row.hashtag,
      trendHashtag,
      'sentiment',
    ),
  }),

  'engagement-spikes': async ({ query }) => {
    const s = scoped(query);
    const spec = { metric: 'engagement', splitBy: 'topic' } as const;
    const byDay = aggregate(s.own, { ...spec, from: s.from, to: s.to, bucket: 'day' });
    const before = comparisonOf(s.ownPrevious, s.previousBounds, byDay, spec);
    return { engagement: timeSeriesOf(byDay, s.from, s.to, 'topic', before) };
  },

  'keyword-mentions-frequency': async ({ query, params }) => {
    const s = scoped(query);
    const page = pageKeywordHits(
      searchKeywords(keywordFrequencies(s.own), params.search ?? ''),
      params,
    );
    /* Per keyword, not a second page: the comparison period's page would hold other keywords. */
    const before = s.ownPrevious
      ? new Map(keywordFrequencies(s.ownPrevious).map((k) => [k.key, k.mentions]))
      : null;
    return {
      ...page,
      keywords: page.keywords.map((k) => ({
        ...k,
        previous: before ? (before.get(k.key) ?? 0) : null,
      })),
    };
  },
} satisfies Partial<WidgetLoaders>;
